import type { FC, PointerEvent, ReactNode } from 'react'

import { useRef } from 'react'
import { motion, useSpring } from 'motion/react'
import { cn } from '@/lib/utils'
import { useReduceMotion } from '@/hooks/use-reduce-motion'

interface MagneticProps {
    children: ReactNode
    strength?: number
    className?: string
}

// Pulls its child gently toward the mouse. Touch and reduced-motion visitors get a normal, still button.
const Magnetic: FC<MagneticProps> = ({ children, strength = 0.28, className }): ReactNode => {
    const ref = useRef<HTMLDivElement>(null)
    const reduce = useReduceMotion()
    const x = useSpring(0, { stiffness: 260, damping: 17, mass: 0.5 })
    const y = useSpring(0, { stiffness: 260, damping: 17, mass: 0.5 })

    const handleMove = (event: PointerEvent<HTMLDivElement>) => {
        if (reduce || event.pointerType !== 'mouse' || !ref.current) return

        const rect = ref.current.getBoundingClientRect()

        x.set((event.clientX - rect.left - rect.width / 2) * strength)
        y.set((event.clientY - rect.top - rect.height / 2) * strength)
    }

    const handleLeave = () => {
        x.set(0)
        y.set(0)
    }

    return (
        <motion.div
            ref={ref}
            onPointerMove={handleMove}
            onPointerLeave={handleLeave}
            style={{ x, y }}
            className={cn('inline-flex', className)}
        >
            {children}
        </motion.div>
    )
}

export default Magnetic
